import { useAPI } from '@/services/useAPI.js';
import { getUserFriendlyErrorMessage } from '@/utils/errorHandler.js';
import { showNotification } from '@/plugins/notification';
import { groupPosts, groupPostsReply } from '@/utils/groupPosts';
import { authErrorHandler } from '@/services/error/authHandler.js';

// Enhanced function to fetch replies for posts in org feeds (similar to main feed)
async function enhanceOrgPostsWithReplies(posts, opts = {}) {
  const { concurrency = 4 } = opts;
  const integratedReplyIds = new Set();

  // Process concurrency helper
  const mapWithConcurrency = async (tasks, limit, mapper) => {
    const results = [];
    for (let i = 0; i < tasks.length; i += limit) {
      const batch = tasks.slice(i, i + limit);
      const batchResults = await Promise.all(batch.map(mapper));
      results.push(...batchResults);
    }
    return results;
  };

  // Prepare mapping tasks only for non-reply posts; keep replies as-is
  const tasks = posts.map(async (post) => {
    if (post.type === 'reply') return post;
    try {
      const repliesResponse = await fetch(`/api/posts/${post.id}/replies`);
      if (!repliesResponse.ok) {
        // Log 503 and other HTTP errors but don't break the flow
        if (repliesResponse.status === 503) {
          if (import.meta.env.MODE === 'development') {
            console.warn(`[enhanceOrgPostsWithReplies] Service unavailable for post ${post.id} replies (503)`);
          }
        } else {
          console.warn(`[enhanceOrgPostsWithReplies] Failed to fetch replies for post ${post.id}: ${repliesResponse.status}`);
        }
        return post;
      }

      const repliesData = await repliesResponse.json();
      const serverReplies = Array.isArray(repliesData.results) ? repliesData.results.slice().reverse() : [];
      
      if (serverReplies.length > 0) {
        // Mark these reply IDs as integrated so they don't appear standalone
        serverReplies.forEach((reply) => {
          if (reply.id) integratedReplyIds.add(reply.id);
        });
        return {
          ...post,
          hasIntegratedReplies: true,
          integratedReplies: serverReplies,
          totalRepliesCount: post.numReplies || repliesData.total || serverReplies.length,
        };
      }
      return post;
    } catch (error) {
      // Catch network errors, timeouts, and other fetch failures
      if (import.meta.env.MODE === 'development') {
        console.debug(`[enhanceOrgPostsWithReplies] Network error for post ${post.id}:`, error.message || error);
      }
      return post;
    }
  });

  // Execute with concurrency cap
  const enhanced = await mapWithConcurrency(tasks, concurrency, (task) => task);
  // Filter out standalone replies that we integrated into parents
  return enhanced.filter((p) => !(p?.type === 'reply' && integratedReplyIds.has(p.id)));
}

export default {
  async fetchOrgTypes({ commit }) {
    const response = await fetch(`/api/organization-types`);
    let data = await response.json();
    commit('setOrgTypes', data);
  },

  async fetchOrgData({ commit }, id) {
    const response = await fetch(`/api/organizations/${id}`);
    let data = await response.json();
    commit('setOrgData', data);
    return response;
  },

  async fetchBannedUsers({ commit }, id) {
    const response = await fetch(`/api/organizations/${id}/banned-users`);
    let data = await response.json();
    commit('setBannedUsers', data);
    return response;
  },

  async clearOrgData({ commit }, id) {
    let data = {};
    commit('setOrgData', data);
  },

  async fetchCurrentProfileOrg({ commit }, data) {
    commit('setCurrentProfileOrg', data);
  },

  async fetchNumFollowersChannel({ commit }, id) {
    const response = await fetch(`/api/channels/${id}`);
    let data = await response.json();
    commit('setNumFollowersChannel', data.numFollowers);
  },

  async fetchOnlyUserOrgs({ commit }, params = {}) {
    let search = params.search || '';
    let data;
    if (search.startsWith('@')) {
      const cleanSearch = search.substring(1);
      const queryParams = new URLSearchParams({
        limit: '100',
        search: cleanSearch,
        autocomplete: 'true',
      });
      const response = await fetch(`/api/organizations/my?${queryParams.toString()}`);
      data = await response.json();
      if (data?.message === "User account is deleted" || data?.message === "User no longer exists" || data?.message === "Unauthorized") {
          await fetch("/api/auth/logout", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          });

          localStorage.removeItem("user");
          localStorage.removeItem("activeTab");
          commit("setAuthStatus", false);

          window.location.href = "/";
          return;
        }
      commit('setMyOrgList', data.results);
      if (!data.results || data.results.length === 0) {
        commit('setNoSearchOrgs', search);
      } else {
        commit('setNoSearchOrgs', null);
      }
      return;
    }
    let queryParams = new URLSearchParams({
      limit: '100',
    });
    if (search) {
      queryParams.set('search', search);
    }
    let response = await fetch(`/api/organizations/my?${queryParams.toString()}`);
    data = await response.json();
    if (data?.message === "User account is deleted" || data?.message === "User no longer exists" || data?.message === "Unauthorized") {
        await fetch("/api/auth/logout", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        localStorage.removeItem("user");
        localStorage.removeItem("activeTab");
        commit("setAuthStatus", false);

        window.location.href = "/";
        return;
        }
    if (search && (!data.results || data.results.length === 0)) {
      let fallbackParams = new URLSearchParams({
        limit: '100',
        search: search,
        autocomplete: 'true',
      });
      response = await fetch(`/api/organizations/my?${fallbackParams.toString()}`);
      data = await response.json();
    }
    commit('setMyOrgList', data.results);
    if (search && (!data.results || data.results.length === 0)) {
      commit('setNoSearchOrgs', search);
    } else {
      commit('setNoSearchOrgs', null);
    }
  },

  async fetchOtherOrgs({ commit }, params = {}) {
    let search = params.search || '';
    let data;
    if (search.startsWith('@')) {
      const cleanSearch = search.substring(1);
      const queryParams = new URLSearchParams({
        limit: '100',
        search: cleanSearch,
        autocomplete: 'true',
      });
      const response = await fetch(`/api/organizations/other?${queryParams.toString()}`);
      data = await response.json();
      if (data?.message === "User account is deleted" || data?.message === "User no longer exists" || data?.message === "Unauthorized") {
        await fetch("/api/auth/logout", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        localStorage.removeItem("user");
        localStorage.removeItem("activeTab");
        commit("setAuthStatus", false);

        window.location.href = "/";
        return;
        }
      commit('setOtherOrgList', data.results);
      if (!data.results || data.results.length === 0) {
        commit('setNoSearchOrgs', search);
      } else {
        commit('setNoSearchOrgs', null);
      }
      return;
    }
    let queryParams = new URLSearchParams({
      limit: '100',
    });
    if (search) {
      queryParams.set('search', search);
    }
    let response = await fetch(`/api/organizations/other?${queryParams.toString()}`);
    data = await response.json();
    if (search && (!data.results || data.results.length === 0)) {
      let fallbackParams = new URLSearchParams({
        limit: '100',
        search: search,
        autocomplete: 'true',
      });
      response = await fetch(`/api/organizations/other?${fallbackParams.toString()}`);
      data = await response.json();
    }
    commit('setOtherOrgList', data.results);
    if (search && (!data.results || data.results.length === 0)) {
      commit('setNoSearchOrgs', search);
    } else {
      commit('setNoSearchOrgs', null);
    }
  },

  async fetchProfOrgByCode({ commit }, code) {
    const response = await fetch(`/api/organizations/by-code/${code}`);
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setOrgData', data);
    }
    return response;
  },

  async fetchCreateOrg({ commit }, payload) {
    let response = await fetch(`/api/organizations`, {
      method: 'POST',
      body: JSON.stringify({
        // Support both new boolean form and legacy ref/object form
        isPrivate:
          typeof payload.isPrivate === 'boolean'
            ? payload.isPrivate
            : (payload.isPrivate && typeof payload.isPrivate === 'object' && 'value' in payload.isPrivate)
              ? payload.isPrivate.value
              : payload.isPrivate,
        name: payload.name,
        username: payload.username,
        email: payload.email,
        description: payload.description,
        typeId: payload.typeId,
        avatarFileId: payload.avatarFileId,
        terms: payload.terms,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setOrgData', data);
    }
    return response;
  },

  async fetchUpdateOrg({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.orgId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        // Accept boolean or ref/object (backwards compatibility)
        isPrivate: typeof payload.isPrivate === 'boolean'
          ? payload.isPrivate
          : payload.isPrivate && typeof payload.isPrivate === 'object' && 'value' in payload.isPrivate
            ? payload.isPrivate.value
            : undefined,
        name: payload.name ? payload.name : undefined,
        username: payload.username ? payload.username : undefined,
        email: payload.email ? payload.email : undefined,
        description: payload.description ? payload.description : undefined,
        typeId: payload.typeId ? payload.typeId : undefined,
        avatarFileId: payload.avatarFileId ? payload.avatarFileId : undefined,
        terms: payload.terms ? payload.terms : undefined,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      if (payload.type !== 'edit') {
        showNotification('success', `Success`, 'Group has been created', 7000);
      } else {
        showNotification('success', `Success`, 'Group has been updated', 7000);
      }
    }
    return response;
  },

  async fetchAddUser({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.orgId}/invite-member`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
        role: payload.role.value,
        hidden: payload.hidden,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setOrgData', data);
      showNotification('success', `Success`, 'Member has been added.', 7000);
    }
    return response;
  },

  async fetchUpdateUser({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.orgId}/edit-member`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
        role: payload.role,
        hidden: payload.hidden,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setOrgData', data);
      if (payload.type === 'display') {
        showNotification(
          'success',
          `Profile visibility`,
          "This member's role visibility on their profile has been successfully updated.",
          7000,
        );
      } else {
        showNotification('success', `Role updated`, "This member's role has been successfully changed.", 7000);
      }
    }
    return response;
  },

  async fetchAcceptInvite({ commit }, id) {
    let response = await fetch(`/api/organizations/${id}/accept-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Member has been added.', 7000);
    }
    return response;
  },

  async fetchDeclineInvite({ commit }, id) {
    let response = await fetch(`/api/organizations/${id}/decline-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Invitation has been declined.', 7000);
    }
    return response;
  },

  async fetchCancelInvite({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.id}/cancel-invitation`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Invitation has been canceled.', 7000);
    }
    return response;
  },

  async fetchDeleteMember({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.id}/remove-member`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Member has been deleted.', 7000);
    }
    return response;
  },

  async fetchBanMember({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.id}/ban`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Member has been banned.', 7000);
    }
    return response;
  },

  async fetchUnbannedMember({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.id}/unban`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Member has been unbanned.', 7000);
    }
    return response;
  },

  async fetchSearchMembers({ commit }, query) {
    if (query.trim().length < 2 && query !== '') {
      commit('setSearchMembers', []);
      commit('setNoSearchMembers', null);
      return;
    }
    try {
      let users = [];
      let originalQuery = query;
      if (query.startsWith('@')) {
        const cleanQuery = query.substring(1);
        const userParams = new URLSearchParams({ search: cleanQuery, autocomplete: 'true' });
        if (!cleanQuery || cleanQuery === '') {
          return;
        }
        const userResponse = await fetch(`/api/users?${userParams}`);
        const userData = await userResponse.json();
        users = Array.isArray(userData) ? userData : userData.results || [];
      } else {
        const params = new URLSearchParams({ query });
        const response = await fetch(`/api/search?${params}`);
        const data = await response.json();
        users = Array.isArray(data) ? data.filter(item => item.collection === 'users').map(item => item.render) : [];
      }
      // Если ничего не найдено, пробуем с @
      if (!users.length && !query.startsWith('@')) {
        const cleanQuery = query;
        const userParams = new URLSearchParams({ search: cleanQuery, autocomplete: 'true' });
        const userResponse = await fetch(`/api/users?${userParams}`);
        const userData = await userResponse.json();
        users = Array.isArray(userData) ? userData : userData.results || [];
      }
      // Filter unique users by id
      const uniqueUsers = [];
      const seenIds = new Set();
      for (const user of users) {
        if (!seenIds.has(user.id)) {
          uniqueUsers.push(user);
          seenIds.add(user.id);
        }
      }
      commit('setSearchMembers', uniqueUsers);
      if (!uniqueUsers.length) {
        commit('setNoSearchMembers', originalQuery);
      } else {
        commit('setNoSearchMembers', null);
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      console.error('Error fetching search results:', friendlyMessage);
      commit('setSearchMembers', []);
      commit('setNoSearchMembers', null);
      
      // In development, also log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Original search error:', error);
      }
    }
  },

  async fetchFollowChannel({ commit, rootState }, payload) {
    let response = await fetch(`/api/users/follow-channel`, {
      method: 'POST',
      body: JSON.stringify({
        channelId: payload.channelId,
        followType: payload.followType,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit("addFollowingChannel", { channelId: payload.channelId, followType: payload.followType });

      try {
        let user = rootState.auth?.user;
        if (user) {
          if (typeof user === 'string') {
            try { user = JSON.parse(user); } catch { }
          }
          user = user || {};
          user.followingChannels = user.followingChannels || [];
          user.followingChannels.push({ channelId: payload.channelId, followType: payload.followType });
          commit('setUserData', user);
        }
      } catch (err) {
        console.error('Failed to sync auth user followingChannels after follow:', err);
      }
      if (payload.followType === 'follow') {
        showNotification(
          'success',
          `Success`,
          'You are now following this group. Its posts will appear in your Following feed.',
          7000,
        );
      } else {
        showNotification(
          'success',
          `Success`,
          'You are now monitoring this group. Its posts will appear in your Monitoring feed.',
          7000,
        );
      }
    }
    return response;
  },

  async fetchUnFollowChannel({ commit, rootState }, payload) {
    let response = await fetch(`/api/users/unfollow-channel`, {
      method: 'POST',
      body: JSON.stringify({
        channelId: payload.channelId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit("removeFollowingChannel", payload.channelId);
      try {
        let user = rootState.auth?.user;
        if (user) {
          if (typeof user === 'string') {
            try { user = JSON.parse(user); } catch { }
          }
          user = user || {};
          user.followingChannels = (user.followingChannels || []).filter(fc => fc.channelId !== payload.channelId);
          commit('setUserData', user);
        }
      } catch (err) {
        console.error('Failed to sync auth user followingChannels after unfollow:', err);
      }
      showNotification(
        'success',
        `Success`,
        'You have unfollowed this group. Its posts will no longer appear in your feeds.',
        7000,
      );
    }
    return response;
  },

  addCollectionOrg({ commit }, { collectionName, data, isReply }) {
    commit('setCollectionOrg', { collectionName, data, isReply });
  },
  async fetchOrgPosts({ commit, state }, { id, limit, channel, type }) {
    // Если загрузка уже идет, не делаем повторный запрос
    if (state.isLoading) {
      return;
    }

    // Сначала устанавливаем признак загрузки
    commit('setStatusLoad', true);

    try {
      // Очищаем коллекцию перед загрузкой
      commit('setOrgPosts', []);

      const params = new URLSearchParams({
        limit: limit || 15,
        channelId: channel ? channel.id : id,
      }).toString();
      let postType;
      if (type !== 'Reply') {
        postType = '&type=post&type=quote&type=repost';
      } else {
        postType = '&type=reply';
      }
      let response = await fetch(`/api/posts?${params}${postType}`);
      let posts = await response.json();

      if (posts.statusCode) {
        authErrorHandler(posts);
      }

      if (postType === '&type=reply') {
        posts.results = await groupPostsReply(posts.results);
      }
      if (posts.count > 0) {
        // First set the initial posts
        commit('setOrgPosts', posts.results);
        
        // Then enhance with integrated replies (like main feed does)
        if (postType !== '&type=reply') {
          try {
            const enhanced = await enhanceOrgPostsWithReplies(posts.results, { concurrency: 6 });
            if (Array.isArray(enhanced) && enhanced.length) {
              commit('setOrgPosts', enhanced);
            }
          } catch (e) {
            console.debug('enhanceOrgPostsWithReplies failed:', e?.message);
          }
        }
      }

      return posts;
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      console.error('Error fetching organization posts:', friendlyMessage);
      
      // In development, also log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Original organization posts error:', error);
      }
      
      return { results: [], count: 0 };
    } finally {
      // В любом случае снимаем признак загрузки
      commit('setStatusLoad', false);
    }
  },

  async fetchNextPaginationOrgPosts({ commit }, payload) {
    if (!payload) {
      throw new Error('Payload is missing or invalid');
    }

    const params = new URLSearchParams({
      limit: '15',
      channelId: payload.channelId,
      after: payload.lastId,
    });

    let response;
    let posts;
    try {
      switch (payload?.tab) {
        case 'orgPosts':
          let postOrgType;
          if (payload?.type === 0) {
            postOrgType = '&type=post&type=quote&type=repost';
          } else {
            postOrgType = '&type=reply';
          }
          response = await fetch(`/api/posts?${params}${postOrgType}`);
          posts = await response.json();
          break;
        default:
          throw new Error('Unknown tabId');
      }

      if (posts.statusCode) {
        authErrorHandler(posts);
      }
      
      // Enhance with integrated replies for non-reply posts (like main feed pagination does)
      if (payload.tab === 'orgPosts' && payload.type === 0 && posts.results && posts.results.length > 0) {
        try {
          const enhanced = await enhanceOrgPostsWithReplies(posts.results, { concurrency: 6 });
          if (Array.isArray(enhanced) && enhanced.length) {
            // Update the posts results with enhanced data
            posts.results = enhanced;
          }
        } catch (e) {
          console.debug('enhanceOrgPostsWithReplies failed (pagination):', e?.message);
        }
      }
      
      return posts;
    } catch (error) {
      console.error('Error in setNextPagonationPosts:', error);
      throw new Error('Failed to fetch posts. Please try again later.');
    }
  },

  async fetchFilteredOrgPosts({ commit }, payload) {
    const params = new URLSearchParams({
      limit: String(payload?.limit ?? '15'),
      channelId: payload?.channel ? payload.channel.id : String(payload?.id ?? ''),
    }).toString();
    let postType;
    if (payload?.type !== 'reply') {
      postType = '&type=post&type=quote&type=repost';
    } else {
      postType = '&type=reply';
    }
    let response = await fetch(`/api/posts?${params}${postType}`);
    let posts = await response.json();
    if (posts?.statusCode) authErrorHandler(posts);
    return posts;
  },

  async fetchDeleteOrg({ commit }, id) {
    let response = await fetch(`/api/organizations/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      showNotification('error', `Error`, data?.message || 'Failed to delete group', 7000);
    } else {
      showNotification('success', `Success`, 'The group has been successfully deleted.', 7000);
    }
    return response;
  },

  async fetchJoinRequests({ commit }, id) {
    let response = await fetch(`/api/organizations/${id}/join-requests`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setJoinRequests', data);
    }
  },

  async fetchRequestToJoin({ commit }, id) {
    let response = await fetch(`/api/organizations/${id}/request-to-join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Request sent`, 'Your request to join this group has been submitted.', 7000);
    }
    return response;
  },

  async fetchLeaveOrg({ commit, rootState }, id) {
    let response = await fetch(`/api/organizations/${id}/leave`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification(
        'success',
        `Left group`,
        "You have been removed from the group's member list and no longer have access to member-specific features.",
        7000,
      );
      
      try {
        commit('removeFollowingChannel', id);

        let user = rootState.auth?.user;
        if (user) {
          if (typeof user === 'string') {
            try { user = JSON.parse(user); } catch { }
          }
          user = user || {};
          user.followingChannels = (user.followingChannels || []).filter(fc => fc.channelId !== id);
          commit('setUserData', user);
        }
      } catch (err) {
        console.error('Failed to sync state after leaving org:', err);
      }
    }
    return response;
  },

  async fetchAcceptJoinRequest({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.channelId}/accept-join-request`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification(
        'success',
        `Request approved`,
        "The user has been approved and is now part of the group's member list.",
        7000,
      );
    }
    return response;
  },

  async fetchDeclineJoinRequest({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.channelId}/decline-join-request`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Request declined`, 'The user has been declined.', 7000);
    }
    return response;
  },
};
